<template>
  <ScheduleControlRow
    :title="title"
    :summary="summary"
    @select="emit('select')"
    @settings="emit('settings')"
  >
    <template #avatar>
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-600 text-sm font-semibold text-white"
        data-cy="selected-count-badge"
      >
        {{ resources.length }}
      </span>
    </template>
  </ScheduleControlRow>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { ResourceAPIResponse } from "@/typings/scheduler";
import ScheduleControlRow from "./ScheduleControlRow.vue";
import { summaryPeriodLabel } from "./summaryPeriod";

/**
 * The control row for a selection of several schedules — TB-2250's deferred
 * fourth state, alongside its Unassigned and single-technician rows.
 *
 * It stands in for an avatar with a count badge rather than a stack of faces:
 * the row is one line tall and the selection is unbounded, so any face-stack
 * would have to truncate at a number this component cannot know.
 */
const props = defineProps<{
  resources: ResourceAPIResponse[];
  date: Date;
}>();

const emit = defineEmits<{
  (e: "select"): void;
  (e: "settings"): void;
}>();

// Deliberately not "N technicians selected": the Unassigned pseudo-row can be
// one of the chosen schedules, and it is not a technician.
const title = computed(() => `${props.resources.length} selected`);

/**
 * The department is named only when every chosen schedule shares one. A mixed
 * selection says nothing rather than naming the first technician's department,
 * which would read as a filter that is not applied.
 */
const sharedDepartment = computed(() => {
  // Every schedule has to agree, not just the ones that carry a department:
  // dropping the blanks first would let a lone named department stand for a
  // selection that also holds Unassigned.
  const names = props.resources.map((resource) => resource.department?.name);
  const [first] = names;
  return first && names.every((name) => name === first) ? first : null;
});

const summary = computed(() =>
  [sharedDepartment.value, "side by side", summaryPeriodLabel(props.date)]
    .filter(Boolean)
    .join(" · ")
);
</script>
